/**
 * @module DemoModeCeremonyModal
 * @purpose Acknowledgement + PIN + current-password ceremony for switching platform Demo
 *   Mode on or off. Built on the house `ConfirmDialog` (focus trap / aria-modal conventions).
 * @depends components/ConfirmDialog, use-demo-mode-ceremony, react-i18next
 * @dependedOnBy pages/platform-settings/DemoModeCard
 *
 * @rules
 * - Performance: pure display + one ceremony hook -- no independent data fetching
 * - State: form state lives in useDemoModeCeremony; open/close is caller-controlled
 * - Errors: backend error codes are mapped to translated, user-facing copy -- the raw
 *   code is never rendered (house rule: clear user-facing stop messages)
 */

import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useDemoModeCeremony, type DemoModeCeremonyErrorCode } from './use-demo-mode-ceremony'
import { DemoModeErrorCodes } from '../../types/platform'
import type { PlatformStatus } from '../../types/platform'

const FIELD_GAP = '16px'

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid var(--border)',
  background: 'var(--bg)',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: '0.875rem',
}

const LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-dim)',
  display: 'block',
  marginBottom: '4px',
}

const BODY_TEXT_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  color: 'var(--text-dim)',
  fontSize: '0.875rem',
}

const ERROR_TEXT_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  color: 'rgb(220, 38, 38)',
  fontSize: '0.8rem',
}

function errorMessageFor(
  code: DemoModeCeremonyErrorCode | null,
  t: TFunction<'admin'>
): string | null {
  if (code === null) {
    return null
  }
  if (code === DemoModeErrorCodes.DEMO_MODE_DENIED) {
    return t('demoMode.error.denied')
  }
  if (code === DemoModeErrorCodes.DEMO_MODE_RATE_LIMITED) {
    return t('demoMode.error.rateLimited')
  }
  if (code === DemoModeErrorCodes.DEMO_MODE_PIN_NOT_CONFIGURED) {
    return t('demoMode.error.pinNotConfigured')
  }
  if (code === DemoModeErrorCodes.DEMO_MODE_UNSUPPORTED_ENVIRONMENT) {
    return t('demoMode.error.unsupportedEnvironment')
  }
  return t('demoMode.error.generic')
}

export interface DemoModeCeremonyModalProps {
  open: boolean
  targetEnabled: boolean
  onClose: () => void
  onSuccess: (status: PlatformStatus) => void
}

interface CeremonyFieldsProps {
  bodyText: string
  errorMessage: string | null
  ceremony: ReturnType<typeof useDemoModeCeremony>
  t: TFunction<'admin'>
}

function CeremonyFields({
  bodyText,
  errorMessage,
  ceremony,
  t,
}: CeremonyFieldsProps): React.ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: FIELD_GAP }}>
      <p style={BODY_TEXT_STYLE}>{bodyText}</p>
      {errorMessage !== null && (
        <p role="alert" style={ERROR_TEXT_STYLE}>
          {errorMessage}
        </p>
      )}
      <div>
        <label htmlFor="demo-mode-pin" style={LABEL_STYLE}>
          {t('demoMode.field.pin')}
        </label>
        <input
          id="demo-mode-pin"
          type="password"
          value={ceremony.pin}
          onChange={(event) => {
            ceremony.setPin(event.target.value)
          }}
          disabled={ceremony.submitting}
          autoComplete="off"
          style={INPUT_STYLE}
        />
      </div>
      <div>
        <label htmlFor="demo-mode-password" style={LABEL_STYLE}>
          {t('demoMode.field.password')}
        </label>
        <input
          id="demo-mode-password"
          type="password"
          value={ceremony.password}
          onChange={(event) => {
            ceremony.setPassword(event.target.value)
          }}
          disabled={ceremony.submitting}
          autoComplete="current-password"
          style={INPUT_STYLE}
        />
      </div>
    </div>
  )
}

/**
 * Ceremony modal for the Demo Mode enable/disable toggle.
 *
 * @behaviour Renders acknowledgement copy (distinct enable/disable wording) above a PIN
 *            field and a current-password field. Confirming calls `toggleDemoMode` via
 *            `useDemoModeCeremony`; on success the modal resets and closes.
 * @errors Failures render a translated message inline; the raw backend code never reaches
 *         the DOM.
 */
export function DemoModeCeremonyModal({
  open,
  targetEnabled,
  onClose,
  onSuccess,
}: DemoModeCeremonyModalProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')
  const ceremony = useDemoModeCeremony({
    targetEnabled,
    onSuccess: (status) => {
      onSuccess(status)
      onClose()
    },
  })

  const handleCancel = (): void => {
    ceremony.reset()
    onClose()
  }

  const titleKey = targetEnabled ? 'demoMode.enable.title' : 'demoMode.disable.title'
  const bodyKey = targetEnabled ? 'demoMode.enable.body' : 'demoMode.disable.body'
  const errorMessage = errorMessageFor(ceremony.errorCode, t)

  return (
    <ConfirmDialog
      open={open}
      title={t(titleKey)}
      confirmLabel={ceremony.submitting ? tCommon('actions.loading') : tCommon('actions.confirm')}
      cancelLabel={tCommon('actions.cancel')}
      onConfirm={() => {
        void ceremony.submit()
      }}
      onCancel={handleCancel}
      destructive
      body={<CeremonyFields bodyText={t(bodyKey)} errorMessage={errorMessage} ceremony={ceremony} t={t} />}
    />
  )
}
